// Amplify's `signIn` refuses to run while a user is already signed in, and signing out to re-auth would defeat
// the purpose (we're trying to *avoid* kicking the user out). So we talk to Cognito's InitiateAuth /
// RespondToAuthChallenge endpoints directly, purely to *prove* the user can still authenticate, and discard the
// tokens the ceremony returns — the existing Amplify session is left untouched.
//
// The USER_AUTH wire format (challenge names, credential JSON shape) mirrors aws-amplify's own passkey flow
// (@aws-amplify/auth client/flows/userAuth + client/utils/passkey/serde) so a passkey assertion serializes
// exactly the way Cognito expects.

const USER_POOL_ID = process.env.REACT_APP_COGNITO_USER_POOL_ID as string
const CLIENT_ID = process.env.REACT_APP_COGNITO_APP_CLIENT_ID as string
const REGION = (USER_POOL_ID || '').split('_')[0] || 'us-east-2'
const ENDPOINT = `https://cognito-idp.${REGION}.amazonaws.com/`

interface CognitoError extends Error {
  code: string
}

interface CognitoResponse {
  ChallengeName?: string
  ChallengeParameters?: { CREDENTIAL_REQUEST_OPTIONS?: string; [key: string]: string | undefined }
  AuthenticationResult?: Record<string, unknown>
  Session?: string
  __type?: string
  message?: string
}

// Raw Cognito Identity Provider JSON call. InitiateAuth / RespondToAuthChallenge are unsigned public APIs, so no
// SigV4 signing is needed.
const cognito = async (target: string, body: Record<string, unknown>): Promise<CognitoResponse> => {
  const res = await fetch(ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}`
    },
    body: JSON.stringify(body)
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    const code =
      String(json.__type || '')
        .split('#')
        .pop() || 'CognitoError'
    const err = new Error(json.message || code) as CognitoError
    err.code = code
    err.name = code
    throw err
  }
  return json
}

// --- base64url helpers (WebAuthn transfers binary as base64url strings) ---

const base64UrlToArrayBuffer = (value: string): ArrayBuffer => {
  const padded = value.length % 4 === 0 ? value : value + '='.repeat(4 - (value.length % 4))
  const binary = atob(padded.replace(/-/g, '+').replace(/_/g, '/'))
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer
}

const arrayBufferToBase64Url = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

interface CredentialRequestOptionsJson {
  challenge: string
  allowCredentials?: { id: string; type: string; transports?: AuthenticatorTransport[] }[]
  [key: string]: unknown
}

// Turn Cognito's CREDENTIAL_REQUEST_OPTIONS JSON into a PublicKeyCredentialRequestOptions. We keep every field
// as-is (rpId, timeout, userVerification, allowCredentials transports) and only decode the binary ones, so any
// of the user's registered passkeys — including the one being removed — is a valid responder.
const toRequestOptions = (input: CredentialRequestOptionsJson): PublicKeyCredentialRequestOptions =>
  ({
    ...input,
    challenge: base64UrlToArrayBuffer(input.challenge),
    allowCredentials: (input.allowCredentials || []).map((cred) => ({
      ...cred,
      id: base64UrlToArrayBuffer(cred.id),
      type: 'public-key' as const
    }))
  } as PublicKeyCredentialRequestOptions)

// Serialize the WebAuthn assertion back to the JSON shape Cognito's RespondToAuthChallenge CREDENTIAL expects.
const serializeAssertion = (credential: PublicKeyCredential): string => {
  const response = credential.response as AuthenticatorAssertionResponse
  const json: Record<string, unknown> = {
    id: credential.id,
    rawId: arrayBufferToBase64Url(credential.rawId),
    type: credential.type,
    clientExtensionResults: credential.getClientExtensionResults(),
    response: {
      clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
      authenticatorData: arrayBufferToBase64Url(response.authenticatorData),
      signature: arrayBufferToBase64Url(response.signature),
      ...(response.userHandle ? { userHandle: arrayBufferToBase64Url(response.userHandle) } : {})
    }
  }
  if (credential.authenticatorAttachment) json.authenticatorAttachment = credential.authenticatorAttachment
  return JSON.stringify(json)
}

/**
 * Prove control of the account with a passkey. Runs the WebAuthn assertion ceremony against Cognito and resolves
 * only if Cognito accepts it. Throws on cancel / no passkey / verification failure. Tokens are discarded.
 */
export const reauthenticateWithPasskey = async (username: string): Promise<void> => {
  const init = await cognito('InitiateAuth', {
    AuthFlow: 'USER_AUTH',
    ClientId: CLIENT_ID,
    AuthParameters: { USERNAME: username, PREFERRED_CHALLENGE: 'WEB_AUTHN' }
  })
  if (init.ChallengeName !== 'WEB_AUTHN' || !init.ChallengeParameters?.CREDENTIAL_REQUEST_OPTIONS) {
    throw new Error('Passkey verification is unavailable for this account.')
  }
  const options = toRequestOptions(JSON.parse(init.ChallengeParameters.CREDENTIAL_REQUEST_OPTIONS))
  const credential = (await navigator.credentials.get({ publicKey: options })) as PublicKeyCredential | null
  if (!credential) throw new Error('No passkey was provided.')

  const done = await cognito('RespondToAuthChallenge', {
    ChallengeName: 'WEB_AUTHN',
    ClientId: CLIENT_ID,
    Session: init.Session,
    ChallengeResponses: { USERNAME: username, CREDENTIAL: serializeAssertion(credential) }
  })
  if (!done.AuthenticationResult) throw new Error('Passkey verification failed.')
}

export type PasswordReauthResult = { status: 'done' } | { status: 'mfa'; session: string }

/**
 * Prove control of the account with a password. On this MFA-required pool Cognito answers a correct password with
 * a TOTP challenge — the caller then completes it via {@link reauthenticateSubmitTotp}. Throws on a wrong password.
 */
export const reauthenticateWithPassword = async (username: string, password: string): Promise<PasswordReauthResult> => {
  const res = await cognito('InitiateAuth', {
    AuthFlow: 'USER_AUTH',
    ClientId: CLIENT_ID,
    AuthParameters: { USERNAME: username, PASSWORD: password, PREFERRED_CHALLENGE: 'PASSWORD' }
  })
  if (res.AuthenticationResult) return { status: 'done' }
  if (res.ChallengeName === 'SOFTWARE_TOKEN_MFA' && res.Session) return { status: 'mfa', session: res.Session }
  throw new Error('Could not verify your password.')
}

/**
 * Complete the TOTP step of a password re-auth. Throws on a wrong/expired code. Tokens are discarded.
 */
export const reauthenticateSubmitTotp = async (username: string, code: string, session: string): Promise<void> => {
  const done = await cognito('RespondToAuthChallenge', {
    ChallengeName: 'SOFTWARE_TOKEN_MFA',
    ClientId: CLIENT_ID,
    Session: session,
    ChallengeResponses: { USERNAME: username, SOFTWARE_TOKEN_MFA_CODE: code }
  })
  if (!done.AuthenticationResult) throw new Error('Verification failed.')
}
